import { useQueries, useQuery } from '@tanstack/react-query';
import { memo, useMemo } from 'react';

import CoupleComparison from './CoupleComparison';
import TableComparison from './TableComparison';
import VerticalTableComparison from './VerticalTableComparison';

import { useStatDefinitions } from '@hooks/useStatDefinitions';
import { useWindowSize } from '@hooks/useWindowSize';
import { getPlayersByIdQuery } from '@queries/players';
import { getSinglePlayerStatsQuery } from '@queries/statistics';
import type { RoundPhase } from '@shared-types/match.types';

export type PlayerComparisonItem = [playerId: number, cup: number, season: number, phase?: RoundPhase];

export type PlayerComparisonOptions = {
  isStory?: boolean;
  playerCompSeason: PlayerComparisonItem[];
  type: 'table' | 'radar';
  statTypes: {
    [key: string]: string[];
  };
};

export const PlayerComparison = ({
  options: { playerCompSeason, statTypes, isStory },
}: {
  options: PlayerComparisonOptions;
}) => {
  const { isMobile } = useWindowSize();

  const playerIds = useMemo(() => playerCompSeason?.map(([pId]) => `${pId}`), [playerCompSeason]);
  const seasons = useMemo(() => playerCompSeason?.map(([_p, _c, season]) => season), [playerCompSeason]);
  const { data: players } = useQuery({
    ...getPlayersByIdQuery({ playerIds }),
    enabled: !!playerIds?.length,
  });

  const statTypesByRole = useMemo(() => {
    if (!players?.length) return null;

    if (players.every((p) => p.fieldPosition === players[0].fieldPosition)) {
      const fieldPosition = players[0].fieldPosition.toLowerCase();
      return statTypes?.[fieldPosition];
    }

    return statTypes.unknown;
  }, [players, statTypes]);

  const statDefs = useStatDefinitions(statTypesByRole);
  const { data: stats } = useQueries({
    queries: playerCompSeason?.map((pcs) => ({
      ...getSinglePlayerStatsQuery({
        stats: statTypesByRole,
        playerId: pcs[0]?.toString(),
        competitionId: pcs[1]?.toString(),
        seasonYear: pcs[2]?.toString(),
        phase: pcs[3],
      }),
      enabled: !!statTypesByRole?.length,
    })),
    combine: (results) => ({
      data: results.map((result) => result.data?.[0]).filter(Boolean),
    }),
  });

  // NOT IMPLEMENTED
  // if (type === 'radar') return null;

  if (!playerCompSeason?.length || playerCompSeason?.length < 2) return null;

  if (playerCompSeason?.length === 2)
    return <CoupleComparison stats={stats} statDefs={statDefs} seasons={seasons} isStory={isStory} />;

  if (!isMobile) return <TableComparison stats={stats} statDefs={statDefs} seasons={seasons} isStory={isStory} />;
  return <VerticalTableComparison stats={stats} statDefs={statDefs} seasons={seasons} isStory={isStory} />;
};

export default memo(PlayerComparison);
